import React from 'react';
import Breadcrumbs from '../../../../components/Breadcrumbs/Breadcrumbs';
import { Link } from 'react-router-dom';
import DocumentIndex from '../../Document/DocumentIndex/DocumentIndex';
import useAjaxTable from '../../../../common/hooks/ajaxHooks/useAjaxTable/useAjaxTable';
import SalesCredit from '../../../../api/controllers/Financial/SalesCredit/SalesCredit';
import NumberHelper from '../../../../utils/NumberHelper/NumberHelper';
import DateHelper from '../../../../utils/DateHelper/DateHelper';
import { useNavigate } from 'react-router';
import LineItemHelper from '../../../../utils/LineItemHelper/LineItemHelper';
import IconButton from '../../../../common/button/IconButton/IconButton';
import { faPlus } from '@fortawesome/free-solid-svg-icons';

const newHeaders = [
  { text: 'Entity', sortingAttribute: 'entity' },
  { text: 'Job', sortingAttribute: 'job' },
  { text: 'Total Value', sortingAttribute: 'value' },
  { text: 'Actions' },
];

const draftHeaders = [
  { text: 'Issue Date', sortingAttribute: 'issue_date' },
  { text: 'Document Reference', sortingAttribute: 'doc_ref' },
  { text: 'Their Reference', sortingAttribute: 'their_ref' },
  { text: 'Entity', sortingAttribute: 'entity' },
  { text: 'Job', sortingAttribute: 'jobs' },
  { text: 'Total Value', sortingAttribute: 'value' },
];

const completedHeaders = [
  { text: 'Issue Date', sortingAttribute: 'issue_date' },
  { text: 'Due Date', sortingAttribute: 'due_date' },
  { text: 'Document Reference', sortingAttribute: 'doc_ref' },
  { text: 'Their Reference', sortingAttribute: 'their_ref' },
  { text: 'Entity', sortingAttribute: 'entity' },
  { text: 'Job', sortingAttribute: 'job' },
  { text: 'Total Value', sortingAttribute: 'value' },
  { text: 'Sage URN', sortingAttribute: 'sage_urn' },
];

const SalesCreditNoteIndex = () => {
  const navigate = useNavigate();
  const { dataNew, sortNew, onSortNew, onSearchNew, pageElementsNew, pageTextNew, refreshNew } =
    useAjaxTable({
      call: SalesCredit.potential,
      name: 'New',
      defaultSort: { sort: newHeaders[0].sortingAttribute, order: 'asc' },
    });

  const {
    dataDraft,
    sortDraft,
    onSortDraft,
    onSearchDraft,
    pageElementsDraft,
    pageTextDraft,
    refreshDraft,
  } = useAjaxTable({
    call: SalesCredit.draft,
    name: 'Draft',
    defaultSort: { sort: draftHeaders[0].sortingAttribute, order: 'desc' },
  });

  const {
    dataCompleted,
    sortCompleted,
    onSortCompleted,
    onSearchCompleted,
    pageElementsCompleted,
    pageTextCompleted,
    refreshCompleted,
  } = useAjaxTable({
    call: SalesCredit.completed,
    name: 'Completed',
    defaultSort: { sort: completedHeaders[0].sortingAttribute, order: 'desc' },
  });

  document.title = 'Sales Credit Notes';

  const handleDrawRowNew = (item, idx) => (
    <tr key={item.id} className={idx % 2 === 0 ? '' : 'coloured'}>
      <td>
        <Link to={`/companies/entities/${item.entity.id}`} target="_blank">
          {item.entity.internalName}
        </Link>
      </td>
      <td>{LineItemHelper.renderSourceLine(item.parent)}</td>
      <td>{NumberHelper.formatNumToCurrency(item.amountOutstanding)}</td>
      <td className={'act-row'}>
        <IconButton
          icon={faPlus}
          text={'Credit Note'}
          onClick={() =>
            navigate(`/financials/sales_credit_notes/new/${item.entity.id}/${item.parent?.id}`)
          }
          className={'invoice-button'}
        />
      </td>
    </tr>
  );

  const handleDrawRowDraft = (item, i) => {
    return (
      <tr key={item.id} className={i % 2 === 0 ? '' : 'coloured'}>
        <td>{item.issueDate && DateHelper.dateOnly(item.issueDate)}</td>
        <td>
          <Link to={`/financials/sales_credit_notes/${item.id}`}>{item.documentReference}</Link>
        </td>
        <td>{item.entityReference}</td>
        <td>
          <Link to={`/companies/entities/${item.entity.id}`} target="_blank">
            {item.entity.internalName}
          </Link>
        </td>
        <td>
          <Link to={`/jobs/bookings/${item.booking?.id}`} target="_blank">
            {item.booking?.jobReference}
          </Link>
        </td>
        <td>{NumberHelper.formatNumToCurrency(item.totalValue)}</td>
      </tr>
    );
  };

  const handleDrawRowCompleted = (item, i) => {
    return (
      <tr key={item.id} className={i % 2 === 0 ? '' : 'coloured'}>
        <td>{item.issueDate && DateHelper.dateOnly(item.issueDate)}</td>
        <td>{item.dueDate && DateHelper.dateOnly(item.dueDate)}</td>
        <td>
          <Link to={`/financials/sales_credit_notes/${item.id}`}>{item.documentReference}</Link>
        </td>
        <td>{item.entityReference}</td>
        <td>
          <Link to={`/companies/entities/${item.entity.id}`} target="_blank">
            {item.entity.internalName}
          </Link>
        </td>
        <td>
          <Link to={`/jobs/bookings/${item.booking?.id}`} target="_blank">
            {item.booking?.jobReference}
          </Link>
        </td>
        <td>{NumberHelper.formatNumToCurrency(item.totalValue)}</td>
        <td>{item.apiUrn}</td>
      </tr>
    );
  };

  const newParams = {
    title: 'Create New Sales Credit Note',
    data: dataNew,
    sort: sortNew,
    onSort: onSortNew,
    onSearch: onSearchNew,
    pageElements: pageElementsNew,
    handleDrawRow: handleDrawRowNew,
    pageText: pageTextNew,
    refresh: refreshNew,
    headers: newHeaders,
  };

  const draftParams = {
    title: 'Draft Sales Credit Notes',
    data: dataDraft,
    sort: sortDraft,
    onSort: onSortDraft,
    onSearch: onSearchDraft,
    pageElements: pageElementsDraft,
    handleDrawRow: handleDrawRowDraft,
    pageText: pageTextDraft,
    refresh: refreshDraft,
    headers: draftHeaders,
  };

  const completedParams = {
    title: 'Completed Sales Credit Notes',
    data: dataCompleted,
    sort: sortCompleted,
    onSort: onSortCompleted,
    onSearch: onSearchCompleted,
    pageElements: pageElementsCompleted,
    handleDrawRow: handleDrawRowCompleted,
    pageText: pageTextCompleted,
    refresh: refreshCompleted,
    headers: completedHeaders,
  };

  return (
    <div className="all-sales-credits">
      <Breadcrumbs>
        <Link to={'/financials/sales_credit_notes'}>Sales Credit Notes</Link>
      </Breadcrumbs>
      <DocumentIndex
        newParams={newParams}
        draftParams={draftParams}
        completedParams={completedParams}
      />
    </div>
  );
};

SalesCreditNoteIndex.propTypes = {};

export default SalesCreditNoteIndex;
